import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { service } from "@ember/service";
import BackButton from "discourse/components/back-button";
import Form from "discourse/components/form";
import { popupAjaxError } from "discourse/lib/ajax-error";
import DButton from "discourse/ui-kit/d-button";
import { i18n } from "discourse-i18n";

export default class LiveRoomForm extends Component {
  @service site;
  @service siteSettings;
  @service chatApi;

  @tracked isSaving = false;
  @tracked chatChannels = [];

  get isAdminContext() {
    return !this.args.onSubmit;
  }

  get formData() {
    return {
      name: this.args.room?.name || "",
      slug: this.args.room?.slug || "",
      description: this.args.room?.description || "",
      public: this.args.room?.public ?? false,
      room_type: this.args.room?.room_type || "open",
      max_participants: this.args.room?.max_participants || null,
      video_enabled: this.args.room?.video_enabled ?? true,
      livekit_enabled: this.args.room?.livekit_enabled ?? false,
      chat_channel_id: this.args.room?.chat_channel_id || null,
      chat_idle_minutes: this.args.room?.chat_idle_minutes ?? 15,
      max_quality_profile:
        this.args.room?.max_quality_profile || "site_default",
    };
  }

  get showVideoToggle() {
    return this.site.live_video_available;
  }

  get showLivekitToggle() {
    return this.site.live_livekit_per_room_available;
  }

  get showChatSettings() {
    return (
      this.siteSettings.chat_enabled && this.siteSettings.live_chat_enabled
    );
  }

  get maxParticipantsValidation() {
    return "integer|number:2,200";
  }

  get roomTypeOptions() {
    return [
      {
        id: "open",
        name: i18n("live.room.type_open"),
        description: i18n("live.room.type_open_description"),
      },
      {
        id: "stage",
        name: i18n("live.room.type_stage"),
        description: i18n("live.room.type_stage_description"),
      },
    ];
  }

  get qualityProfileOptions() {
    return ["site_default", "standard", "high", "maximum"].map((profile) => ({
      id: profile,
      name:
        profile === "site_default"
          ? i18n("live.admin.room.quality_site_default")
          : i18n(`live.quality.${profile}`),
    }));
  }

  get submitLabel() {
    if (this.isAdminContext) {
      return this.args.room?.id ? "live.admin.update" : "live.admin.create";
    }
    return "live.room.save";
  }

  @action
  async loadChatChannels() {
    if (!this.showChatSettings) {
      return;
    }
    try {
      const collection = this.chatApi.channels();
      await collection.load();
      this.chatChannels = (collection.items ?? []).filter(
        (channel) => channel.threadingEnabled
      );
    } catch (e) {
      popupAjaxError(e);
    }
  }

  isStageType(roomType) {
    return roomType === "stage";
  }

  @action
  async handleSubmit(data) {
    this.isSaving = true;

    try {
      if (this.args.onSubmit) {
        await this.args.onSubmit(data);
      } else {
        const room = this.args.room;
        room.setProperties(data);
        await room.save();
        this.args.onSave?.(room);
      }
    } catch (e) {
      popupAjaxError(e);
    } finally {
      this.isSaving = false;
    }
  }

  <template>
    <div class="live-room-form {{if this.isAdminContext 'admin-detail'}}">
      {{#if this.isAdminContext}}
        <BackButton
          class="live-admin-back"
          @label="live.admin.back"
          @route="adminPlugins.show.live-rooms.index"
        />
      {{/if}}

      <Form
        class="live-room-form__form"
        @data={{this.formData}}
        @onSubmit={{this.handleSubmit}}
        as |form data|
      >
        <form.Field
          @format="full"
          @name="name"
          @placeholder={{i18n "live.admin.room.name_placeholder"}}
          @title={{i18n "live.admin.room.name"}}
          @type="input"
          @validation="required|length:1,80"
          as |field|
        >
          <field.Control />
        </form.Field>

        <form.Field
          @description={{i18n "live.admin.room.slug_help"}}
          @format="full"
          @name="slug"
          @placeholder={{i18n "live.admin.room.slug_placeholder"}}
          @title={{i18n "live.admin.room.slug"}}
          @type="input"
          as |field|
        >
          <field.Control />
        </form.Field>

        <form.Field
          @format="full"
          @name="description"
          @title={{i18n "live.admin.room.description"}}
          @type="textarea"
          as |field|
        >
          <field.Control />
        </form.Field>

        <form.Field
          @format="full"
          @name="room_type"
          @title={{i18n "live.admin.room.room_type"}}
          @type="radio-group"
          as |field|
        >
          <field.Control as |radioGroup|>
            {{#each this.roomTypeOptions as |option|}}
              <radioGroup.Radio @value={{option.id}}>
                <strong>{{option.name}}</strong>
                —
                {{option.description}}
              </radioGroup.Radio>
            {{/each}}
          </field.Control>
        </form.Field>

        {{#if (this.isStageType data.room_type)}}
          <div class="live-room-form__stage-hint">
            {{i18n "live.room.type_stage_hint"}}
          </div>
        {{/if}}

        <form.Field
          @description={{i18n "live.admin.room.public_help"}}
          @format="full"
          @name="public"
          @title={{i18n "live.admin.room.public"}}
          @type="toggle"
          as |field|
        >
          <field.Control />
          {{#if @onManageMembers}}
            <DButton
              class="btn-link live-room-form__manage-members
                {{if data.public '--hidden'}}"
              @action={{@onManageMembers}}
              @icon="users"
              @label="live.admin.room.manage_members"
            />
          {{/if}}
        </form.Field>

        {{#if this.showVideoToggle}}
          <form.Field
            @description={{if
              (this.isStageType data.room_type)
              (i18n "live.admin.room.video_enabled_stage_help")
              (i18n "live.admin.room.video_enabled_help")
            }}
            @format="full"
            @name="video_enabled"
            @title={{i18n "live.admin.room.video_enabled"}}
            @type="toggle"
            as |field|
          >
            <field.Control />
          </form.Field>
        {{/if}}

        {{#if this.showLivekitToggle}}
          <form.Field
            @description={{i18n "live.admin.room.livekit_enabled_help"}}
            @format="full"
            @name="livekit_enabled"
            @title={{i18n "live.admin.room.livekit_enabled"}}
            @type="toggle"
            as |field|
          >
            <field.Control />
          </form.Field>
        {{/if}}

        <form.Field
          @description={{i18n "live.admin.room.max_participants_help"}}
          @name="max_participants"
          @title={{i18n "live.admin.room.max_participants"}}
          @type="input-number"
          @validation={{this.maxParticipantsValidation}}
          as |field|
        >
          <field.Control />
        </form.Field>

        <details class="live-room-form__quality">
          <summary>{{i18n "live.admin.room.quality_section"}}</summary>

          <form.Field
            @description={{i18n "live.admin.room.max_quality_profile_help"}}
            @format="full"
            @name="max_quality_profile"
            @title={{i18n "live.admin.room.max_quality_profile"}}
            @type="select"
            as |field|
          >
            <field.Control @includeNone={{false}} as |select|>
              {{#each this.qualityProfileOptions as |option|}}
                <select.Option @value={{option.id}}>
                  {{option.name}}
                </select.Option>
              {{/each}}
            </field.Control>
          </form.Field>
        </details>

        {{#if this.showChatSettings}}
          <div class="live-room-form__chat" {{didInsert this.loadChatChannels}}>
            <form.Field
              @description={{i18n "live.admin.room.chat_channel_help"}}
              @format="full"
              @name="chat_channel_id"
              @title={{i18n "live.admin.room.chat_channel"}}
              @type="select"
              as |field|
            >
              <field.Control as |select|>
                {{#each this.chatChannels as |channel|}}
                  <select.Option
                    @value={{channel.id}}
                  >{{channel.title}}</select.Option>
                {{/each}}
              </field.Control>
            </form.Field>

            {{#unless this.chatChannels.length}}
              <div class="live-room-form__chat-empty">
                {{i18n "live.admin.room.chat_channel_none"}}
              </div>
            {{/unless}}

            {{#if data.chat_channel_id}}
              <form.Field
                @description={{i18n "live.admin.room.chat_idle_minutes_help"}}
                @name="chat_idle_minutes"
                @title={{i18n "live.admin.room.chat_idle_minutes"}}
                @type="input-number"
                @validation="integer|number:2,1440"
                as |field|
              >
                <field.Control />
              </form.Field>

              <div class="live-room-form__chat-preview">
                {{i18n "live.admin.room.chat_thread_hint"}}
              </div>
            {{/if}}
          </div>
        {{/if}}

        <form.Submit
          class="live-room-form__submit"
          @disabled={{this.isSaving}}
          @label={{this.submitLabel}}
        />
      </Form>
    </div>
  </template>
}
